import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Monitor, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { Page, PageBody, PageHeader, Panel, Toolbar } from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { ChipGroup } from '@/ui/Filters';
import { SearchInput } from '@/ui/Field';
import { Button } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { ConfirmDialog } from '@/ui/Dialog';
import { api, errorMessage } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { useShortcutHint } from '@/app/StatusLine';
import { countLabel, num, relativeTime, smartDate } from '@/lib/format';
import {
  QUEUE_ALARM_DEPTH,
  readRegisterHealth,
  registerLifecycle,
  statusIsMisleading,
} from '@/features/system/registers';
import type { RegisterSummary, UserSummary } from '@/lib/api/types';
import { asText } from '@/lib/searchParams';

/**
 * The register fleet.
 *
 * Two audiences on one screen: a manager clearing new enrollments, and anyone
 * asking "is lane 3 actually up?". The second question is why the health column
 * is derived rather than read — see `features/system/registers.ts`. The `status`
 * column is still shown, but as raw evidence, and it is called out when it
 * disagrees with the heartbeat.
 */

type HealthFilter = 'all' | 'attention' | 'live' | 'disabled';

interface RegisterSearch {
  q: string;
  view: HealthFilter;
}

export const Route = createFileRoute('/_app/system/registers')({
  staticData: { crumb: 'Registers' },
  validateSearch: (s: Record<string, unknown>): RegisterSearch => ({
    q: asText(s.q, ''),
    view:
      s.view === 'attention' || s.view === 'live' || s.view === 'disabled'
        ? s.view
        : 'all',
  }),
  component: RegistersScreen,
});

function RegistersScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canApprove = useAtLeast('manager');

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'Enter', label: 'Open register' },
  ]);

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => api.get<RegisterSummary[]>('/registers'),
    // A fleet view is only useful if it is current; the endpoint is one small
    // table read.
    refetchInterval: 15_000,
  });

  // Heartbeats carry a cashier id, not a name. One users read resolves every
  // lane's "who is on it" without an N+1.
  const staff = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
    staleTime: 5 * 60_000,
  });

  const cashierName = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of staff.data ?? []) {
      map.set(u.id, `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.username);
    }
    return map;
  }, [staff.data]);

  const all = registers.data ?? [];
  // Pending = never approved and still active (approvedAt, not the status
  // column, since TASK-0207). A rejected enrollment is listed with the fleet.
  const pending = all.filter((r) => registerLifecycle(r) === 'pending');
  const enrolled = all.filter((r) => registerLifecycle(r) !== 'pending');

  const rows = useMemo(() => {
    const term = search.q.trim().toLowerCase();
    return enrolled.filter((r) => {
      if (term) {
        const haystack =
          `${r.name} ${r.stationNumber ?? ''} ${r.appVersion ?? ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      const reading = readRegisterHealth(r);
      if (search.view === 'live') return reading.health === 'live';
      if (search.view === 'disabled') return reading.health === 'revoked';
      if (search.view === 'attention') {
        return (
          reading.health === 'stale' ||
          reading.health === 'silent' ||
          (r.transactionQueueDepth ?? 0) > 0
        );
      }
      return true;
    });
  }, [enrolled, search.q, search.view]);

  const attentionCount = enrolled.filter((r) => {
    const reading = readRegisterHealth(r);
    return (
      reading.health === 'stale' ||
      reading.health === 'silent' ||
      (r.transactionQueueDepth ?? 0) > 0
    );
  }).length;

  const misleading = enrolled.filter((r) => statusIsMisleading(r, readRegisterHealth(r)));

  function patch(next: Partial<RegisterSearch>) {
    void navigate({ search: (prev: RegisterSearch) => ({ ...prev, ...next }) });
  }

  const columns: Array<Column<RegisterSummary>> = [
    {
      id: 'name',
      header: 'Register',
      width: 'minmax(0, 1.4fr)',
      cell: (r) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-ink truncate">{r.name}</span>
          {r.stationNumber !== null && r.stationNumber !== undefined ? (
            <Code>#{r.stationNumber}</Code>
          ) : null}
        </div>
      ),
    },
    {
      id: 'health',
      header: 'Health',
      width: '7rem',
      cell: (r) => {
        const reading = readRegisterHealth(r);
        return (
          <span title={reading.detail}>
            <Badge tone={reading.tone} dot pulse={reading.livePulse}>
              {reading.label}
            </Badge>
          </span>
        );
      },
    },
    {
      id: 'heartbeat',
      header: 'Heartbeat',
      width: '7rem',
      mono: true,
      cell: (r) => {
        const reading = readRegisterHealth(r);
        return (
          <span
            className={
              reading.health === 'silent'
                ? 'text-negative'
                : reading.health === 'stale'
                  ? 'text-warn'
                  : 'text-ink-muted'
            }
            title={r.lastHeartbeat ?? 'never'}
          >
            {r.lastHeartbeat ? relativeTime(r.lastHeartbeat) : 'never'}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status col.',
      width: '6.5rem',
      hideBelow: 'xl',
      cell: (r) => {
        const stale = statusIsMisleading(r, readRegisterHealth(r));
        return (
          <span
            className={
              stale ? 'text-2xs text-ink-faint line-through' : 'text-2xs text-ink-faint'
            }
            title={
              stale
                ? 'Written by the last heartbeat — not a live signal. Trust the heartbeat column.'
                : undefined
            }
          >
            {r.status}
          </span>
        );
      },
    },
    {
      id: 'queue',
      header: 'Queue',
      width: '5rem',
      align: 'right',
      mono: true,
      cell: (r) => {
        const depth = r.transactionQueueDepth ?? 0;
        if (!depth) return <span className="text-ink-faint">0</span>;
        return (
          <span className={depth >= QUEUE_ALARM_DEPTH ? 'text-negative' : 'text-warn'}>
            {num(depth)}
          </span>
        );
      },
    },
    {
      id: 'sync',
      header: 'Last sync',
      width: '7rem',
      mono: true,
      hideBelow: 'lg',
      cell: (r) => (
        <span className="text-ink-muted">
          {r.lastSyncAt ? smartDate(r.lastSyncAt) : '—'}
        </span>
      ),
    },
    {
      id: 'shift',
      header: 'On lane',
      width: 'minmax(0, 1fr)',
      hideBelow: 'lg',
      cell: (r) => {
        if (!r.currentShiftId) return <span className="text-ink-faint">—</span>;
        const name = r.currentCashierId ? cashierName.get(r.currentCashierId) : null;
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="text-ink-muted truncate">
              {name ?? (r.currentCashierId ? `User ${r.currentCashierId}` : 'Unassigned')}
            </span>
            <Code>shift {r.currentShiftId}</Code>
          </span>
        );
      },
    },
    {
      id: 'version',
      header: 'App',
      width: '6rem',
      mono: true,
      hideBelow: 'md',
      cell: (r) =>
        r.appVersion ? (
          <span className="text-ink-muted">{r.appVersion}</span>
        ) : (
          <span className="text-ink-faint" title="This build does not report a version">
            unknown
          </span>
        ),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="System"
        title="Registers"
        subtitle={`${countLabel(all.length, 'register')} enrolled`}
      />

      <Toolbar>
        <SearchInput
          size="sm"
          value={search.q}
          onValueChange={(q) => patch({ q })}
          placeholder="Filter by name, station or version…"
          className="w-72"
        />
        <ChipGroup<HealthFilter>
          value={search.view}
          onChange={(view) => patch({ view })}
          options={[
            { value: 'all', label: 'All', count: enrolled.length },
            {
              value: 'attention',
              label: 'Needs attention',
              count: attentionCount,
              tone: attentionCount > 0 ? 'warn' : undefined,
            },
            { value: 'live', label: 'Live' },
            { value: 'disabled', label: 'Disabled' },
          ]}
        />
      </Toolbar>

      <PageBody className="flex min-h-0 flex-col gap-3">
        {pending.length > 0 ? (
          <PendingApprovals registers={pending} canApprove={canApprove} />
        ) : null}

        {misleading.length > 0 ? (
          <Callout tone="warn" icon={<ShieldAlert />} title="The status column is lying">
            {countLabel(misleading.length, 'register')} still read{' '}
            <code className="font-mono">online</code> but have not sent a heartbeat in
            minutes. Every heartbeat from an approved register rewrites that column to{' '}
            <code className="font-mono">online</code> and nothing ever writes it back — so
            freshness, not status, is the signal. The health column above is derived from
            the heartbeat.
          </Callout>
        ) : null}

        <Panel title="Fleet" bleed className="min-h-0 flex-1">
          <DataTable
            rows={registers.isLoading ? undefined : rows}
            columns={columns}
            rowKey={(r) => r.id}
            loading={registers.isLoading}
            error={registers.error}
            onRetry={() => void registers.refetch()}
            onRowOpen={(r) =>
              void navigate({ to: '/system/registers/$id', params: { id: String(r.id) } })
            }
            rowTone={(r) => {
              const reading = readRegisterHealth(r);
              if (reading.health === 'silent') return 'negative';
              if (reading.health === 'stale') return 'warn';
              if ((r.transactionQueueDepth ?? 0) >= QUEUE_ALARM_DEPTH) return 'negative';
              return null;
            }}
            empty={{
              icon: <Monitor />,
              title:
                search.q || search.view !== 'all'
                  ? 'No matching registers'
                  : 'No registers',
              description:
                search.q || search.view !== 'all'
                  ? 'Clear the filter to see the whole fleet.'
                  : 'A register enrolls itself the first time it handshakes with the server.',
            }}
            footer={
              <>
                <span>{countLabel(rows.length, 'register')}</span>
                {registers.isFetching ? (
                  <span className="text-signal-ink">refreshing…</span>
                ) : null}
              </>
            }
          />
        </Panel>
      </PageBody>
    </Page>
  );
}

/* ── Pending approval ─────────────────────────────────────────────────────────
   A pending register has handshaked but the server refuses everything except
   its heartbeat (no catalog, sync or payments) until a manager approves it.
   That is the single most time-critical thing on this screen, so it sits
   above the fleet with the action inline.                                   */

function PendingApprovals({
  registers,
  canApprove,
}: {
  registers: RegisterSummary[];
  canApprove: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [rejecting, setRejecting] = useState<RegisterSummary | null>(null);

  const approve = useMutation({
    mutationFn: (id: number) => api.post<RegisterSummary>(`/registers/${id}/approve`),
    onSuccess: (register) => {
      void queryClient.invalidateQueries({ queryKey: ['registers'] });
      toast.success(`${register.name} approved`, {
        description: 'It can now sync, take payments and pull the catalog.',
      });
    },
    onError: (error) =>
      toast.error('Could not approve', { description: errorMessage(error) }),
  });

  const reject = useMutation({
    mutationFn: (id: number) =>
      api.post<RegisterSummary>(`/registers/${id}/reject`, { expectedStatus: 'pending' }),
    onSuccess: (register) => {
      void queryClient.invalidateQueries({ queryKey: ['registers'] });
      setRejecting(null);
      toast.success(`${register.name} rejected`, {
        description: 'Disabled and deactivated.',
      });
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: ['registers'] });
      setRejecting(null);
      // A 409 means the row moved under us — someone else approved it, or the
      // register re-handshaked. Re-read and tell the operator; never retry.
      toast.error('Rejection did not apply', {
        description: `${errorMessage(error)} The list has been re-read — check the register's current state before acting again.`,
      });
    },
  });

  return (
    <>
      <Panel
        title="Pending approval"
        description="Enrolled, but refused everything except its heartbeat until a manager approves it."
        bleed
        className="border-warn/35"
      >
        <ul className="divide-line-soft divide-y">
          {registers.map((r) => (
            <li key={r.id} className="flex items-center gap-3 px-3 py-2">
              <Badge tone="warn" dot>
                Pending
              </Badge>
              <button
                className="min-w-0 flex-1 text-left"
                onClick={() =>
                  void navigate({
                    to: '/system/registers/$id',
                    params: { id: String(r.id) },
                  })
                }
              >
                <span className="text-ink block truncate text-sm hover:underline">
                  {r.name}
                </span>
                <span className="text-2xs text-ink-faint block truncate">
                  Station {r.stationNumber ?? '—'} · enrolled {relativeTime(r.createdAt)}
                  {r.appVersion ? ` · v${r.appVersion}` : ''}
                </span>
              </button>
              {canApprove ? (
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Check className="size-3.5" />}
                    loading={approve.isPending && approve.variables === r.id}
                    onClick={() => approve.mutate(r.id)}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    icon={<X className="size-3.5" />}
                    onClick={() => setRejecting(r)}
                  >
                    Reject
                  </Button>
                </div>
              ) : (
                <span className="text-2xs text-ink-faint shrink-0">
                  A manager must approve this.
                </span>
              )}
            </li>
          ))}
        </ul>
      </Panel>

      <ConfirmDialog
        open={!!rejecting}
        onOpenChange={(open) => !open && setRejecting(null)}
        title={`Reject ${rejecting?.name ?? ''}?`}
        description="The enrollment is refused for good. This hardware stays locked out until the record is retired and the register enrolls again."
        confirmLabel="Reject enrollment"
        loading={reject.isPending}
        consequences={[
          'Sets status to disabled and active to false',
          'Register-authed requests from this hardware are refused',
          'The hardware id is kept, so a re-handshake reuses this record and stays refused',
          'A rejected enrollment can never be approved later: to enroll this machine again, retire the record and have it handshake again',
        ]}
        onConfirm={() => rejecting && reject.mutate(rejecting.id)}
      />
    </>
  );
}
